/**
 * API Client - Base configuration and request handling
 */

import { API_CONFIG, HTTP_STATUS, STORAGE_KEYS } from '../constants';

// Custom error class for API errors
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.isUnauthorized = status === HTTP_STATUS.UNAUTHORIZED;
    this.isForbidden = status === HTTP_STATUS.FORBIDDEN;
    this.isNotFound = status === HTTP_STATUS.NOT_FOUND;
    this.isServerError = status >= HTTP_STATUS.INTERNAL_SERVER_ERROR;
    this.isNetworkError = status === 0;
  }
}

// Default headers
const defaultHeaders = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

/**
 * Get auth token from storage
 * Looks for JWT token stored under STORAGE_KEYS.AUTH_TOKEN
 * @returns {string|null} - JWT token if found in localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Build headers with optional auth
 * Automatically includes Authorization header with Bearer token if available.
 * This is used for all protected endpoints that require JWT authentication.
 * @param {Object} customHeaders - Custom headers to merge
 * @param {boolean} includeAuth - Whether to include Authorization header (default: true)
 * @returns {Object} - Headers object with Authorization header if token exists
 */
const buildHeaders = (customHeaders = {}, includeAuth = true) => {
  const headers = { ...defaultHeaders, ...customHeaders };
  
  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      // JWT automatically included as Bearer token for protected endpoints
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
};

/**
 * Parse response based on content type
 */
const parseResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  
  return response.text();
};

/**
 * Handle API response and errors
 */
const handleResponse = async (response) => {
  const data = await parseResponse(response);
  
  if (!response.ok) {
    const errorMessage = data?.message || data?.error || `HTTP Error: ${response.status}`;
    throw new ApiError(errorMessage, response.status, data);
  }
  
  return data;
};

/**
 * Main request function
 */
const request = async (endpoint, options = {}) => {
  const {
    method = 'GET',
    body = null,
    headers = {},
    includeAuth = true,
    timeout = API_CONFIG.TIMEOUT,
    ...fetchOptions
  } = options;

  const url = endpoint.startsWith('http') ? endpoint : `${API_CONFIG.BASE_URL}${endpoint}`;
  
  const config = {
    method,
    headers: buildHeaders(headers, includeAuth),
    ...fetchOptions,
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  config.signal = controller.signal;

  try {
    const response = await fetch(url, config);
    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new ApiError('Request timeout', 408);
    }
    
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Network error or other fetch error
    throw new ApiError(error.message || 'Network error', 0);
  }
};

/**
 * API client with HTTP method shortcuts
 */
const apiClient = {
  get: (endpoint, options = {}) => 
    request(endpoint, { ...options, method: 'GET' }),
  
  post: (endpoint, body, options = {}) => 
    request(endpoint, { ...options, method: 'POST', body }),
  
  put: (endpoint, body, options = {}) => 
    request(endpoint, { ...options, method: 'PUT', body }),
  
  patch: (endpoint, body, options = {}) => 
    request(endpoint, { ...options, method: 'PATCH', body }),
  
  delete: (endpoint, options = {}) => 
    request(endpoint, { ...options, method: 'DELETE' }),
};

export default apiClient;
